import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageLoading } from '@/components/ui/loading';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { deriveProjectAvailability, PROJECT_AVAILABILITY } from '@shared/project-availability';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { formatDeleteError } from '@/lib/api';

type AvailabilityFilter = 'all' | 'active' | 'upcoming' | 'closed';

const AVAILABILITY_LABELS: Record<AvailabilityFilter, string> = {
  all: 'All',
  active: 'Active',
  upcoming: 'Upcoming',
  closed: 'Closed',
};

function statusVariant(availability: string) {
  switch (availability) {
    case PROJECT_AVAILABILITY.ACTIVE: return 'approved' as const;
    case PROJECT_AVAILABILITY.UPCOMING: return 'draft' as const;
    default: return 'warning' as const;
  }
}

export default function ProjectsListPage() {
  const [filter, setFilter] = useState<AvailabilityFilter>('all');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [confirmName, setConfirmName] = useState('');
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const qc = useQueryClient();
  const canDelete = hasPermission('projects:delete');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-projects'],
    queryFn: async () => {
      const r = await api.get<{ data: any[] }>('/projects');
      return r.data ?? [];
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => { addToast('Project permanently deleted.', 'success'); qc.invalidateQueries({ queryKey: ['admin-projects'] }); setDeleteTarget(null); setConfirmName(''); },
    onError: (e: unknown) => { addToast(formatDeleteError(e, 'project'), 'error'); },
  });

  const rows = useMemo(() => {
    const all = (data ?? []).map((p: any) => ({
      ...p,
      availability: deriveProjectAvailability(p.project_start_date, p.project_close_date),
    }));
    if (filter === 'all') return all;
    return all.filter((p: any) => p.availability === filter);
  }, [data, filter]);

  if (isLoading) return <PageLoading />;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-primary">Projects</h1>
        <Link to="/admin/projects/new"><Button className="gap-2"><Plus size={16} />Add Project</Button></Link>
      </div>
      <Card><CardContent className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
          {(['all', 'active', 'upcoming', 'closed'] as AvailabilityFilter[]).map((value) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={filter === value ? 'default' : 'outline'}
              onClick={() => setFilter(value)}
            >
              {AVAILABILITY_LABELS[value]}
            </Button>
          ))}
        </div>
        <Table>
          <TableHeader><TableRow><TableHead className="w-[34%]">Name</TableHead><TableHead className="w-[16%]">Client</TableHead><TableHead className="w-[12%]">Start Date</TableHead><TableHead className="w-[12%]">Close Date</TableHead><TableHead className="w-[14%]">Status</TableHead><TableHead className="w-[12%] text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((p: any) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell>{p.client_name || '-'}</TableCell>
                <TableCell>{p.project_start_date ?? p.start_date ?? '-'}</TableCell>
                <TableCell>{p.project_close_date ?? p.expected_end_date ?? '-'}</TableCell>
                <TableCell><Badge variant={statusVariant(p.availability)}>{p.availability}</Badge></TableCell>
                <TableCell className="text-right space-x-1">
                  <Link to={`/admin/projects/${p.id}/edit`}><Button variant="ghost" size="icon" aria-label={`Edit ${p.name}`}><Pencil size={14} /></Button></Link>
                  {canDelete && (
                    <Button variant="ghost" size="icon" aria-label={`Permanently delete ${p.name}`} title="Permanently delete" onClick={() => { setDeleteTarget(p); setConfirmName(''); }}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setConfirmName(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <strong>{deleteTarget?.name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-secondary">Type the project name to confirm:</p>
            <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={deleteTarget?.name || ''} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteM.isPending || confirmName !== deleteTarget?.name}
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => deleteTarget && deleteM.mutate(deleteTarget.id)}
            >
              {deleteM.isPending ? 'Deleting...' : 'Permanently Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
